/**
 * LFRCORD — Painel dos Fundadores.
 *
 * Central de controle da plataforma, dentro do próprio app: mesma sessão,
 * mesma conta, mesma identidade visual. Não é site à parte nem login separado.
 *
 * Este arquivo é SÓ A TELA. Ele não decide quem entra — quem decide é o
 * servidor, que reconfere fundador em cada evento (`server/adminSocket.ts`).
 * Esconder o botão de quem não é fundador é conveniência; quem forjar o evento
 * à mão bate na mesma porta.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { FLAGS_EMERGENCIA } from "@shared/admin";
import type {
  AdminAuditEntry,
  AdminFlags,
  AdminOverview,
  AdminServer,
  AdminServerDetail,
  AdminUser,
  AdminUserDetail,
  AdminBanido,
  AdminCrescimento,
  SessaoFundador,
} from "@shared/admin";
import { useApp } from "@/state/store";
import { C, FONT_MONO, hexA } from "@/theme";

type Secao =
  | "inicio"
  | "usuarios"
  | "servidores"
  | "moderacao"
  | "auditoria"
  | "seguranca"
  | "banidos"
  | "emergencia"
  | "acesso";

const SECOES: { id: Secao; nome: string }[] = [
  { id: "inicio", nome: "Painel Inicial" },
  { id: "usuarios", nome: "Usuários" },
  { id: "servidores", nome: "Servidores" },
  { id: "moderacao", nome: "Moderação" },
  { id: "auditoria", nome: "Auditoria" },
  { id: "banidos", nome: "Banidos" },
  { id: "seguranca", nome: "Segurança" },
  { id: "emergencia", nome: "Controles de Emergência" },
  { id: "acesso", nome: "Meu Acesso" },
];

/* ---------------------------------------------------------------------- */
/* Peças visuais                                                           */
/* ---------------------------------------------------------------------- */

const ROTULO: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
  textTransform: "uppercase",
};

const CARTAO: CSSProperties = {
  padding: "14px 16px",
  border: `1px solid ${C.lineSoft}`,
  borderRadius: 12,
  background: C.field,
};

const BOTAO: CSSProperties = {
  padding: "8px 14px",
  border: 0,
  borderRadius: 9,
  background: C.accent,
  color: C.ink,
  fontSize: 12.5,
  fontWeight: 700,
  cursor: "pointer",
};

const BOTAO_PERIGO: CSSProperties = { ...BOTAO, background: C.red, color: "#fff" };

const CAMPO: CSSProperties = {
  width: "100%",
  padding: "9px 11px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  background: C.field,
  color: C.textStrong,
  fontSize: 13,
  outline: "none",
};

function Numero({ titulo, valor, nota }: { titulo: string; valor: number | string; nota?: string }): ReactElement {
  return (
    <div style={CARTAO}>
      <div style={ROTULO}>{titulo}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: C.textStrong, marginTop: 6, lineHeight: 1 }}>
        {valor}
      </div>
      {nota ? <div style={{ ...ROTULO, marginTop: 6, color: C.textDim }}>{nota}</div> : null}
    </div>
  );
}

function data(ms: number): string {
  return new Date(ms).toLocaleString("pt-BR");
}

/**
 * Confirmação de duas etapas para o que não se desfaz.
 *
 * O primeiro clique arma e mostra o que vai acontecer; o segundo executa. Num
 * painel onde um clique apaga um servidor inteiro, um `confirm()` do navegador
 * seria fácil demais de aceitar no automático.
 */
function BotaoPerigoso({
  rotulo,
  confirmacao,
  onConfirmar,
  desabilitado,
}: {
  rotulo: string;
  confirmacao: string;
  onConfirmar: () => void;
  desabilitado?: boolean;
}): ReactElement {
  const [armado, setArmado] = useState(false);
  useEffect(() => {
    if (!armado) return;
    // Desarma sozinho: um botão que fica armado indefinidamente vira armadilha
    // para o clique seguinte, que pode ser sobre outra coisa.
    const t = window.setTimeout(() => setArmado(false), 6000);
    return () => window.clearTimeout(t);
  }, [armado]);

  return (
    <button
      type="button"
      disabled={desabilitado}
      onClick={() => {
        if (!armado) {
          setArmado(true);
          return;
        }
        setArmado(false);
        onConfirmar();
      }}
      style={{
        ...BOTAO_PERIGO,
        opacity: desabilitado ? 0.5 : 1,
        cursor: desabilitado ? "not-allowed" : "pointer",
        background: armado ? C.red : "transparent",
        color: armado ? "#fff" : C.red,
        border: `1px solid ${C.red}`,
      }}
    >
      {armado ? confirmacao : rotulo}
    </button>
  );
}

/* ---------------------------------------------------------------------- */
/* Painel                                                                  */
/* ---------------------------------------------------------------------- */

export function PainelFundadores({ onFechar }: { onFechar: () => void }): ReactElement | null {
  const { state, actions } = useApp();
  const [secao, setSecao] = useState<Secao>("inicio");

  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [usuarios, setUsuarios] = useState<AdminUser[]>([]);
  const [busca, setBusca] = useState("");
  const [usuarioAberto, setUsuarioAberto] = useState<AdminUserDetail | null>(null);
  const [servidores, setServidores] = useState<AdminServer[]>([]);
  const [servidorAberto, setServidorAberto] = useState<AdminServerDetail | null>(null);
  const [auditoria, setAuditoria] = useState<AdminAuditEntry[]>([]);
  const [flags, setFlags] = useState<AdminFlags>({});
  const [recado, setRecado] = useState<string | null>(null);
  const [banidos, setBanidos] = useState<AdminBanido[]>([]);
  const [sessoes, setSessoes] = useState<SessaoFundador[]>([]);
  /** Ações chegando ao vivo, as mais novas primeiro. */
  const [aoVivo, setAoVivo] = useState<AdminAuditEntry[]>([]);
  const [crescimento, setCrescimento] = useState<AdminCrescimento>([]);
  /**
   * A senha de confirmação, guardada só em memória e só enquanto o painel
   * está aberto. Nunca vai para o armazenamento do navegador: a graça de
   * pedir senha é justamente ela não estar disponível numa sessão largada.
   */
  const [minhaSenha, setMinhaSenha] = useState("");

  const emit = actions.adminEmit;

  /*
    O erro do painel PRECISA aparecer dentro do painel.

    Toda recusa do servidor vira `lfr:error`, que o app desenha no Toast do
    rodapé — e o Toast vive em `zIndex: 70`, atrás desta tela, que é opaca e
    está em 80. Ou seja: o painel recusava a ação e dizia por quê num aviso
    que ficava escondido debaixo dele. Da cadeira de quem clicava, o botão
    simplesmente não fazia nada. É a razão de "alguns botões não funcionam".
  */
  const erro = state.notice;
  const limparErro = actions.dismissNotice;
  useEffect(() => {
    if (!erro) return;
    const t = window.setTimeout(() => limparErro(), 6000);
    return () => window.clearTimeout(t);
  }, [erro, limparErro]);

  /* Assina as respostas uma vez; o painel inteiro se alimenta daqui. */
  useEffect(() => {
    return actions.adminSubscribe({
      overview: setOverview,
      usuarios: p => setUsuarios(p.usuarios),
      usuario: setUsuarioAberto,
      servidores: p => setServidores(p.servidores),
      servidor: setServidorAberto,
      auditoria: p => setAuditoria(p.entradas),
      flags: p => setFlags(p.flags),
      ok: p => setRecado(p.mensagem),
      banidos: p => setBanidos(p.banidos),
      sessoes: p => setSessoes(p.sessoes),
      // O feed guarda as 50 últimas: é painel de vigilância, não arquivo —
      // para o histórico completo existe a aba Auditoria.
      evento: e => setAoVivo(atual => [e, ...atual].slice(0, 50)),
      crescimento: p => setCrescimento(p.serie),
    });
  }, [actions]);

  /* Cada seção pede o que precisa ao abrir. */
  useEffect(() => {
    if (secao === "inicio") {
      emit("admin:overview", {});
      emit("admin:crescimento", {});
    }
    if (secao === "usuarios") emit("admin:users", { busca: "" });
    if (secao === "servidores") emit("admin:servers", {});
    if (secao === "moderacao" || secao === "auditoria" || secao === "seguranca")
      emit("admin:audit", { limite: 200 });
    if (secao === "emergencia") emit("admin:flags", {});
    if (secao === "banidos") emit("admin:banidos", {});
    if (secao === "seguranca") emit("admin:sessoesFundadores", {});
  }, [secao, emit]);

  /*
    Os números se atualizam sozinhos enquanto a aba inicial estiver aberta.

    O feed ao vivo cobre as AÇÕES DE FUNDADOR — elas são empurradas na hora.
    Mas ninguém empurra "entrou mais uma pessoa" ou "mandaram 40 mensagens":
    esses números vêm de contagem, e sem esta releitura o painel mostraria a
    fotografia do momento em que foi aberto até alguém trocar de aba.

    15 segundos: perto o bastante de tempo real para um painel de vigilância, e
    longe o bastante para não fazer oito COUNT(*) a cada segundo.
  */
  useEffect(() => {
    if (secao !== "inicio") return;
    const timer = window.setInterval(() => emit("admin:overview", {}), 15_000);
    return () => window.clearInterval(timer);
  }, [secao, emit]);

  useEffect(() => {
    if (!recado) return;
    const t = window.setTimeout(() => setRecado(null), 4000);
    return () => window.clearTimeout(t);
  }, [recado]);

  const eu = state.me;
  if (!eu || !state.platformFounder) return null;

  const buscar = (termo: string): void => {
    setBusca(termo);
    emit("admin:users", { busca: termo });
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        display: "flex",
        background: C.stage,
        // A borda de acento é a diferença visual sutil que diz "área exclusiva":
        // o app inteiro por dentro, com uma moldura que não existe em nenhuma
        // outra tela.
        boxShadow: `inset 0 0 0 2px ${hexA(C.accent, 0.55)}`,
      }}
    >
      {/* ---------------- lateral ---------------- */}
      <aside
        style={{
          width: 232,
          flex: "0 0 auto",
          borderRight: `1px solid ${C.lineSoft}`,
          display: "flex",
          flexDirection: "column",
          padding: "16px 12px",
          gap: 4,
          overflowY: "auto",
        }}
      >
        <div style={{ padding: "0 6px 12px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.textStrong, letterSpacing: ".04em" }}>
            LFRCORD
          </div>
          <div style={{ ...ROTULO, marginTop: 3, color: C.accent }}>Painel dos Fundadores</div>
          <div style={{ ...ROTULO, marginTop: 2 }}>Controle global da plataforma</div>
        </div>

        {SECOES.map(item => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              setSecao(item.id);
              setUsuarioAberto(null);
              setServidorAberto(null);
            }}
            style={{
              textAlign: "left",
              padding: "8px 10px",
              border: 0,
              borderRadius: 8,
              cursor: "pointer",
              background: secao === item.id ? C.selected : "transparent",
              color: secao === item.id ? C.textStrong : C.textMuted,
              fontSize: 13,
              fontWeight: secao === item.id ? 700 : 500,
            }}
          >
            {item.nome}
          </button>
        ))}

        <div style={{ flex: 1 }} />
        <button type="button" onClick={onFechar} style={{ ...BOTAO, background: "transparent", color: C.textMuted, border: `1px solid ${C.lineStrong}` }}>
          ← Voltar para o LFRCORD
        </button>
      </aside>

      {/* ---------------- conteúdo ---------------- */}
      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 22px",
            borderBottom: `1px solid ${C.lineSoft}`,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: C.accent,
              color: C.ink,
              display: "grid",
              placeItems: "center",
              fontWeight: 700,
              overflow: "hidden",
            }}
          >
            {eu.avatarUrl ? (
              <img src={eu.avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              eu.name.slice(0, 1).toUpperCase()
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.textStrong }}>{eu.name}</div>
            <div style={{ ...ROTULO, color: C.accent }}>Fundador</div>
          </div>
          <div style={{ flex: 1 }} />
          {erro ? (
            <button
              type="button"
              onClick={limparErro}
              title="Dispensar"
              style={{
                maxWidth: 420,
                textAlign: "left",
                padding: "6px 10px",
                borderRadius: 8,
                cursor: "pointer",
                border: `1px solid ${hexA(C.red, 0.6)}`,
                background: hexA(C.red, 0.12),
                color: C.red,
                fontSize: 12.5,
                lineHeight: 1.35,
              }}
            >
              {erro}
            </button>
          ) : recado ? (
            <div style={{ ...ROTULO, color: C.accent, letterSpacing: ".06em" }}>{recado}</div>
          ) : null}
          {/*
            A senha fica no cabeçalho, digitada uma vez por sessão do painel, em
            vez de um pedido a cada clique. Pedir toda vez cansaria e levaria
            alguém a deixar anotado — o que é pior do que não pedir.
          */}
          <input
            type="password"
            value={minhaSenha}
            onChange={e => setMinhaSenha(e.target.value)}
            placeholder="Sua senha (para ações críticas)"
            style={{
              ...CAMPO,
              width: 220,
              padding: "7px 10px",
              fontSize: 12,
              borderColor: minhaSenha ? hexA(C.accent, 0.5) : C.lineStrong,
            }}
          />
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px 40px" }}>
          {secao === "inicio" ? (
            <Inicio overview={overview} aoVivo={aoVivo} crescimento={crescimento} />
          ) : null}

          {secao === "usuarios" ? (
            <Usuarios
              usuarios={usuarios}
              busca={busca}
              onBuscar={buscar}
              aberto={usuarioAberto}
              onAbrir={id => emit("admin:user", { userId: id })}
              onFechar={() => setUsuarioAberto(null)}
              emit={emit}
              minhaSenha={minhaSenha}
            />
          ) : null}

          {secao === "servidores" ? (
            <Servidores
              servidores={servidores}
              aberto={servidorAberto}
              onAbrir={id => emit("admin:server", { serverId: id })}
              onFechar={() => setServidorAberto(null)}
              emit={emit}
              minhaSenha={minhaSenha}
            />
          ) : null}

          {secao === "moderacao" ? <Moderacao entradas={auditoria} /> : null}
          {secao === "auditoria" ? <Auditoria entradas={auditoria} /> : null}
          {secao === "seguranca" ? (
            <Seguranca entradas={auditoria} overview={overview} sessoes={sessoes} emit={emit} />
          ) : null}
          {secao === "banidos" ? <Banidos banidos={banidos} emit={emit} /> : null}
          {secao === "emergencia" ? (
            <Emergencia flags={flags} emit={emit} minhaSenha={minhaSenha} />
          ) : null}
          {secao === "acesso" ? <MeuAcesso /> : null}
        </div>
      </main>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Seções                                                                  */
/* ---------------------------------------------------------------------- */

function Inicio({
  overview,
  aoVivo,
  crescimento,
}: {
  overview: AdminOverview | null;
  aoVivo: AdminAuditEntry[];
  crescimento: AdminCrescimento;
}): ReactElement {
  if (!overview) return <div style={{ ...ROTULO }}>Carregando…</div>;
  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
        <Numero titulo="Usuários" valor={overview.usuarios} />
        <Numero titulo="Online" valor={overview.online} nota="conectados agora" />
        <Numero titulo="Servidores" valor={overview.servidores} />
        <Numero titulo="Mensagens" valor={overview.mensagens} />
        <Numero titulo="Novos usuários" valor={overview.novosUsuarios} nota="últimos 7 dias" />
        <Numero titulo="Banimentos" valor={overview.banimentos} />
        <Numero titulo="Canais" valor={overview.canais} />
        <Numero titulo="Salas de voz" valor={overview.salasDeVoz} />
      </div>
      {crescimento.length > 0 ? (
        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 10 }}>Crescimento — 14 dias</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 70 }}>
            {crescimento.map(d => {
              const teto = Math.max(...crescimento.map(x => x.mensagens), 1);
              return (
                <div key={d.dia} style={{ flex: 1, display: "grid", gap: 3, justifyItems: "center" }}
                     title={`${d.dia}: ${d.mensagens} mensagens, ${d.contas} contas`}>
                  <div style={{ width: "100%", height: Math.max(2, (d.mensagens / teto) * 52), background: C.accent, borderRadius: 3 }} />
                  {d.contas > 0 ? <div style={{ width: 5, height: 5, borderRadius: 999, background: "#3BA55D" }} /> : <div style={{ height: 5 }} />}
                </div>
              );
            })}
          </div>
          <div style={{ ...ROTULO, marginTop: 8, color: C.textDim }}>
            barra = mensagens do dia · ponto verde = houve cadastro
          </div>
        </section>
      ) : null}

      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Atividade em tempo real</div>
        {aoVivo.length === 0 ? (
          <div style={{ fontSize: 13, color: C.textDim }}>
            Nada aconteceu desde que você abriu o painel. Ações de fundadores aparecem aqui na
            hora em que acontecem.
          </div>
        ) : (
          aoVivo.map(e => <LinhaAuditoria key={e.id} entrada={e} />)
        )}
      </section>

      <div style={{ ...ROTULO }}>Números de {data(overview.em)}, atualizados a cada 15 s</div>
    </div>
  );
}

function Usuarios({
  usuarios,
  busca,
  onBuscar,
  aberto,
  onAbrir,
  onFechar,
  emit,
  minhaSenha,
}: {
  usuarios: AdminUser[];
  busca: string;
  onBuscar: (t: string) => void;
  aberto: AdminUserDetail | null;
  onAbrir: (id: string) => void;
  onFechar: () => void;
  emit: (evento: string, carga: unknown) => void;
  minhaSenha: string;
}): ReactElement {
  const [motivo, setMotivo] = useState("");
  const [senha, setSenha] = useState("");
  const [nota, setNota] = useState("");
  /** Vazio = banimento permanente. Com numero, e suspensao. */
  const [dias, setDias] = useState("");

  if (aberto) {
    const u = aberto.usuario;
    return (
      <div style={{ display: "grid", gap: 16, maxWidth: 780 }}>
        <button type="button" onClick={onFechar} style={{ ...BOTAO, background: "transparent", color: C.textMuted, border: `1px solid ${C.lineStrong}`, justifySelf: "start" }}>
          ← Voltar à lista
        </button>

        <section style={CARTAO}>
          <div style={ROTULO}>Visão geral</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.textStrong, marginTop: 6 }}>
            {u.nome} <span style={{ color: C.textMuted, fontWeight: 500 }}>@{u.handle}</span>
            {u.fundador ? <span style={{ ...ROTULO, color: C.accent, marginLeft: 8 }}>fundador</span> : null}
            {u.banido ? <span style={{ ...ROTULO, color: C.red, marginLeft: 8 }}>banido</span> : null}
          </div>
          <div style={{ ...ROTULO, marginTop: 8, letterSpacing: ".04em" }}>id {u.id}</div>
          <div style={{ ...ROTULO, marginTop: 3, letterSpacing: ".04em" }}>
            conta criada em {data(u.criadoEm)} · {u.servidores} servidor(es) · {u.sessoes} sessão(ões)
          </div>
          {u.banido ? (
            <div style={{ marginTop: 10, fontSize: 13, color: C.red }}>
              {u.motivoDoBanimento ? `Motivo: ${u.motivoDoBanimento}` : "Banido"}
              {u.banidoAte ? ` · volta em ${data(u.banidoAte)}` : " · permanente"}
            </div>
          ) : null}
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Servidores</div>
          {aberto.servidores.length === 0 ? (
            <div style={{ fontSize: 13, color: C.textDim }}>Nenhum.</div>
          ) : (
            aberto.servidores.map(s => (
              <div key={s.id} style={{ fontSize: 13, color: C.textSoft, padding: "3px 0" }}>
                {s.nome} {s.dono ? "· dono" : ""} {s.cargos.length ? `· ${s.cargos.join(", ")}` : ""}
              </div>
            ))
          )}
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Moderação</div>
          <div style={{ display: "grid", gap: 10 }}>
            <input
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Motivo (aparece para a pessoa banida)"
              style={CAMPO}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                value={dias}
                onChange={e => setDias(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="dias"
                style={{ ...CAMPO, width: 90 }}
              />
              <span style={{ ...ROTULO, color: C.textDim }}>
                {dias ? `suspensao de ${dias} dia(s) — volta sozinho` : "vazio = banimento permanente"}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {u.banido ? (
                <button type="button" onClick={() => emit("admin:unban", { userId: u.id })} style={BOTAO}>
                  Revogar banimento
                </button>
              ) : (
                <BotaoPerigoso
                  confirmacao="Confirmar?"
                  desabilitado={u.fundador}
                  rotulo={dias ? `Suspender por ${dias} dia(s)` : "Banir do LFRCORD"}
                  onConfirmar={() =>
                    emit("admin:ban", {
                      userId: u.id,
                      motivo,
                      senha: minhaSenha,
                      dias: dias ? Number(dias) : undefined,
                    })
                  }
                />
              )}
              <button type="button" onClick={() => emit("admin:endSessions", { userId: u.id })} style={{ ...BOTAO, background: "transparent", color: C.textStrong, border: `1px solid ${C.lineStrong}` }}>
                Encerrar sessões
              </button>
            </div>
            {u.fundador ? (
              <div style={{ ...ROTULO, color: C.textDim }}>
                Conta fundadora não pode ser banida pelo painel.
              </div>
            ) : null}
          </div>
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Trocar a senha</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={senha}
              onChange={e => setSenha(e.target.value)}
              placeholder="Nova senha (mínimo 8)"
              style={{ ...CAMPO, flex: 1 }}
            />
            <BotaoPerigoso
              rotulo="Trocar"
              confirmacao="Confirmar troca?"
              desabilitado={senha.length < 8}
              onConfirmar={() => {
                emit("admin:setPassword", { userId: u.id, senha, minhaSenha });
                setSenha("");
              }}
            />
          </div>
          <div style={{ ...ROTULO, marginTop: 8, color: C.textDim }}>
            Trocar a senha encerra todas as sessões da conta.
          </div>
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Sessões ativas</div>
          {aberto.sessoes.length === 0 ? (
            <div style={{ fontSize: 13, color: C.textDim }}>Nenhuma.</div>
          ) : (
            aberto.sessoes.map(s => (
              <div key={s.id} style={{ fontSize: 12.5, color: C.textSoft, padding: "3px 0" }}>
                {data(s.criadaEm)} · {s.agente ?? "aparelho desconhecido"} · {s.ip ?? "sem ip"}
              </div>
            ))
          )}
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Observações internas</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Anotar algo sobre esta conta" style={{ ...CAMPO, flex: 1 }} />
            <button
              type="button"
              style={BOTAO}
              onClick={() => {
                if (!nota.trim()) return;
                emit("admin:note", { userId: u.id, texto: nota });
                setNota("");
              }}
            >
              Anotar
            </button>
          </div>
          {aberto.notas.map(n => (
            <div key={n.id} style={{ fontSize: 13, color: C.textSoft, padding: "4px 0", borderTop: `1px solid ${C.lineSoft}` }}>
              {n.texto}
              <span style={{ ...ROTULO, marginLeft: 8 }}>@{n.autor} · {data(n.criadoEm)}</span>
            </div>
          ))}
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Ações sofridas por esta conta</div>
          {aberto.moderacao.length === 0 ? (
            <div style={{ fontSize: 13, color: C.textDim }}>Nenhuma.</div>
          ) : (
            aberto.moderacao.map(e => <LinhaAuditoria key={e.id} entrada={e} />)
          )}
        </section>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <input
        value={busca}
        onChange={e => onBuscar(e.target.value)}
        placeholder="Procurar por nome, @handle, id ou e-mail"
        style={{ ...CAMPO, maxWidth: 420 }}
      />
      <div style={{ display: "grid", gap: 6 }}>
        {usuarios.map(u => (
          <button
            key={u.id}
            type="button"
            onClick={() => onAbrir(u.id)}
            style={{ ...CARTAO, textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: C.textStrong }}>{u.nome}</span>
              <span style={{ color: C.textMuted, marginLeft: 6, fontSize: 12.5 }}>@{u.handle}</span>
              {u.fundador ? <span style={{ ...ROTULO, color: C.accent, marginLeft: 8 }}>fundador</span> : null}
              {u.banido ? <span style={{ ...ROTULO, color: C.red, marginLeft: 8 }}>banido</span> : null}
            </span>
            <span style={{ ...ROTULO }}>{u.servidores} srv · {u.sessoes} ses</span>
          </button>
        ))}
        {usuarios.length === 0 ? <div style={{ ...ROTULO }}>Nenhuma conta encontrada.</div> : null}
      </div>
    </div>
  );
}

function Servidores({
  servidores,
  aberto,
  onAbrir,
  onFechar,
  emit,
  minhaSenha,
}: {
  servidores: AdminServer[];
  aberto: AdminServerDetail | null;
  onAbrir: (id: string) => void;
  onFechar: () => void;
  emit: (evento: string, carga: unknown) => void;
  minhaSenha: string;
}): ReactElement {
  const [motivo, setMotivo] = useState("");
  const [aviso, setAviso] = useState("");

  if (aberto) {
    const s = aberto.servidor;
    return (
      <div style={{ display: "grid", gap: 16, maxWidth: 780 }}>
        <button type="button" onClick={onFechar} style={{ ...BOTAO, background: "transparent", color: C.textMuted, border: `1px solid ${C.lineStrong}`, justifySelf: "start" }}>
          ← Voltar à lista
        </button>

        <section style={CARTAO}>
          <div style={ROTULO}>Visão geral</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.textStrong, marginTop: 6 }}>{s.nome}</div>
          <div style={{ ...ROTULO, marginTop: 8, letterSpacing: ".04em" }}>id {s.id}</div>
          <div style={{ ...ROTULO, marginTop: 3, letterSpacing: ".04em" }}>
            dono @{s.donoHandle} · {s.membros} membros · {s.canais} canais · {s.salasDeVoz} salas ·{" "}
            {s.cargos} cargos · {s.mensagens} mensagens · criado em {data(s.criadoEm)}
          </div>
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Membros</div>
          {aberto.membros.map(m => (
            <div key={m.id} style={{ fontSize: 13, color: C.textSoft, padding: "3px 0" }}>
              {m.nome} <span style={{ color: C.textMuted }}>@{m.handle}</span>
              {m.cargos.length ? <span style={{ ...ROTULO, marginLeft: 8 }}>{m.cargos.join(", ")}</span> : null}
            </div>
          ))}
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Canais e cargos</div>
          {aberto.canais.map(c => (
            <div key={c.id} style={{ fontSize: 13, color: C.textSoft, padding: "2px 0" }}>
              #{c.nome} · {c.mensagens} mensagens
            </div>
          ))}
          {aberto.salasDeVoz.map(v => (
            <div key={v.id} style={{ fontSize: 13, color: C.textSoft, padding: "2px 0" }}>🔊 {v.nome}</div>
          ))}
          {aberto.cargos.map(r => (
            <div key={r.id} style={{ fontSize: 13, color: C.textSoft, padding: "2px 0" }}>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: r.cor, marginRight: 6 }} />
              {r.nome} · {r.membros} membro(s)
            </div>
          ))}
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Convites</div>
          {aberto.convites.length === 0 ? (
            <div style={{ fontSize: 13, color: C.textDim }}>Nenhum.</div>
          ) : (
            aberto.convites.map(i => (
              <div key={i.code} style={{ fontFamily: FONT_MONO, fontSize: 12.5, color: C.textSoft, padding: "2px 0" }}>
                {i.code} · {i.usos}{i.limite === null ? "" : `/${i.limite}`} uso(s) · por @{i.criadoPor}
                {i.revogado ? " · revogado" : ""}
              </div>
            ))
          )}
        </section>

        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Aviso ao servidor</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={aviso} onChange={e => setAviso(e.target.value)} placeholder="Mensagem para todo mundo deste servidor" style={{ ...CAMPO, flex: 1 }} />
            <button
              type="button"
              style={BOTAO}
              onClick={() => {
                if (!aviso.trim()) return;
                emit("admin:announce", { serverId: s.id, mensagem: aviso });
                setAviso("");
              }}
            >
              Enviar
            </button>
          </div>
        </section>

        <section style={{ ...CARTAO, borderColor: hexA(C.red, 0.4) }}>
          <div style={{ ...ROTULO, marginBottom: 8, color: C.red }}>Zona perigosa</div>
          <input
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder="Motivo (é o que a comunidade vê antes de o servidor sumir)"
            style={{ ...CAMPO, marginBottom: 10 }}
          />
          <BotaoPerigoso
            rotulo={`Apagar ${s.nome}`}
            confirmacao={`Confirmar? ${s.membros} membros e ${s.mensagens} mensagens somem.`}
            onConfirmar={() => {
              emit("admin:deleteServer", { serverId: s.id, motivo, senha: minhaSenha });
              onFechar();
            }}
          />
        </section>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Aviso para TODA a plataforma</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={aviso} onChange={e => setAviso(e.target.value)} placeholder="Chega a todos os servidores e a quem estiver online" style={{ ...CAMPO, flex: 1 }} />
          <button
            type="button"
            style={BOTAO}
            onClick={() => {
              if (!aviso.trim()) return;
              emit("admin:announce", { serverId: null, mensagem: aviso });
              setAviso("");
            }}
          >
            Enviar a todos
          </button>
        </div>
      </section>

      <div style={{ display: "grid", gap: 6 }}>
        {servidores.map(s => (
          <button key={s.id} type="button" onClick={() => onAbrir(s.id)} style={{ ...CARTAO, textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: C.textStrong }}>{s.nome}</span>
              <span style={{ ...ROTULO, marginLeft: 8 }}>dono @{s.donoHandle}</span>
            </span>
            <span style={{ ...ROTULO }}>
              {s.membros} membros · {s.canais} canais · {s.mensagens} msgs
            </span>
          </button>
        ))}
        {servidores.length === 0 ? <div style={{ ...ROTULO }}>Nenhum servidor.</div> : null}
      </div>
    </div>
  );
}

function LinhaAuditoria({ entrada }: { entrada: AdminAuditEntry }): ReactElement {
  const plataforma = entrada.acao.startsWith("platform_");
  return (
    <div style={{ padding: "6px 0", borderTop: `1px solid ${C.lineSoft}` }}>
      <span style={{ ...ROTULO, color: plataforma ? C.accent : C.textFaint }}>{entrada.acao}</span>
      <div style={{ fontSize: 13, color: C.textSoft, marginTop: 2 }}>
        <strong style={{ color: C.textStrong }}>{entrada.autorNome}</strong>
        {entrada.alvoNome ? ` → ${entrada.alvoNome}` : ""} · {entrada.detalhe}
      </div>
      <div style={{ ...ROTULO, marginTop: 2 }}>{data(entrada.em)}</div>
    </div>
  );
}

function Moderacao({ entradas }: { entradas: AdminAuditEntry[] }): ReactElement {
  const punicoes = useMemo(
    () =>
      entradas.filter(e =>
        ["kick", "ban", "timeout", "server_mute", "server_deafen", "platform_ban", "platform_unban"].includes(
          e.acao,
        ),
      ),
    [entradas],
  );
  return (
    <section style={{ ...CARTAO, maxWidth: 780 }}>
      <div style={{ ...ROTULO, marginBottom: 8 }}>Punições recentes</div>
      {punicoes.length === 0 ? (
        <div style={{ fontSize: 13, color: C.textDim }}>Nenhuma punição registrada.</div>
      ) : (
        punicoes.map(e => <LinhaAuditoria key={e.id} entrada={e} />)
      )}
    </section>
  );
}

function Auditoria({ entradas }: { entradas: AdminAuditEntry[] }): ReactElement {
  return (
    <section style={{ ...CARTAO, maxWidth: 780 }}>
      <div style={{ ...ROTULO, marginBottom: 8 }}>Tudo que foi feito, por quem e quando</div>
      {entradas.length === 0 ? (
        <div style={{ fontSize: 13, color: C.textDim }}>Nada registrado ainda.</div>
      ) : (
        entradas.map(e => <LinhaAuditoria key={e.id} entrada={e} />)
      )}
    </section>
  );
}

function Seguranca({
  entradas,
  overview,
  sessoes,
  emit,
}: {
  entradas: AdminAuditEntry[];
  overview: AdminOverview | null;
  sessoes: SessaoFundador[];
  emit: (evento: string, carga: unknown) => void;
}): ReactElement {
  const criticas = entradas.filter(e => e.acao === "platform_ban" || e.acao === "ban");
  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 780 }}>
      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Estado do LFRCORD</div>
        <Servico nome="Aplicativo" ok />
        <Servico nome="Autenticação" ok />
        <Servico nome="Mensagens" ok />
        <Servico nome="Banco de dados" ok />
        <Servico nome="Voz e transmissão" ok />
        <div style={{ ...ROTULO, marginTop: 10, color: C.textDim }}>
          Os serviços respondem porque este painel está aberto — a conexão que o desenha é a
          mesma que os usa. Um monitoramento independente ainda não existe.
        </div>
      </section>

      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Sessões de fundador abertas</div>
        <div style={{ fontSize: 12.5, color: C.textDim, marginBottom: 10, lineHeight: 1.5 }}>
          Uma sessão de fundador num aparelho ou endereço que você não reconhece é o primeiro
          sinal de conta tomada. Encerre a que não for sua.
        </div>
        {sessoes.length === 0 ? (
          <div style={{ fontSize: 13, color: C.textDim }}>Nenhuma sessão registrada.</div>
        ) : (
          sessoes.map(s => (
            <div
              key={s.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 0",
                borderTop: `1px solid ${C.lineSoft}`,
              }}
            >
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.textSoft }}>
                <strong style={{ color: C.textStrong }}>@{s.handle}</strong>
                {s.atual ? <span style={{ ...ROTULO, marginLeft: 8, color: C.accent }}>esta aqui</span> : null}
                <br />
                {s.agente ?? "aparelho desconhecido"} · {s.ip ?? "sem ip"} · desde {data(s.criadaEm)}
              </span>
              {s.atual ? null : (
                <BotaoPerigoso
                  rotulo="Encerrar"
                  confirmacao="Confirmar?"
                  onConfirmar={() => emit("admin:endSession", { sessaoId: s.id })}
                />
              )}
            </div>
          ))
        )}
      </section>

      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Eventos de atenção</div>
        {criticas.length === 0 ? (
          <div style={{ fontSize: 13, color: C.textDim }}>Nada crítico registrado.</div>
        ) : (
          criticas.map(e => <LinhaAuditoria key={e.id} entrada={e} />)
        )}
      </section>

      {overview ? (
        <section style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 8 }}>Contas banidas</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: overview.banimentos > 0 ? C.red : C.textStrong }}>
            {overview.banimentos}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function Servico({ nome, ok }: { nome: string; ok: boolean }): ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: ok ? "#3BA55D" : C.red }} />
      <span style={{ fontSize: 13, color: C.textSoft }}>{nome}</span>
      <span style={{ ...ROTULO, marginLeft: "auto" }}>{ok ? "operacional" : "fora"}</span>
    </div>
  );
}

/**
 * Um interruptor de emergência.
 *
 * Duas coisas que a versão anterior não tinha e custavam caro:
 *
 * 1. **Simetria.** Ligar pedia confirmação; desligar era um clique seco. Mas
 *    desligar o modo manutenção no meio de um incidente é tão grave quanto
 *    ligar — reabre a plataforma inteira. As duas direções confirmam agora, e
 *    as duas dizem exatamente o que vai acontecer.
 *
 * 2. **Resposta ao clique.** Entre clicar e o servidor responder não havia
 *    sinal nenhum, então um clique que ia falhar era indistinguível de um que
 *    ia funcionar. Agora o botão entra em "aplicando" e só sai quando o
 *    servidor devolve o estado — o que vale tanto para o sim quanto para o
 *    não, porque a recusa também devolve os interruptores.
 */
function Interruptor({
  nome,
  descricao,
  ligado,
  aplicando,
  onTrocar,
}: {
  nome: string;
  descricao: string;
  ligado: boolean;
  aplicando: boolean;
  onTrocar: (paraLigado: boolean) => void;
}): ReactElement {
  const [armado, setArmado] = useState(false);

  /* Some sozinho: um botão armado e esquecido é um clique errado esperando. */
  useEffect(() => {
    if (!armado) return;
    const t = window.setTimeout(() => setArmado(false), 4000);
    return () => window.clearTimeout(t);
  }, [armado]);

  /* A resposta do servidor (ou a recusa) desarma e destrava. */
  useEffect(() => {
    setArmado(false);
  }, [ligado]);

  const destino = !ligado;
  const acao = destino ? "Ligar" : "Desligar";
  const confirmacao = destino ? "Ligar mesmo?" : "Desligar mesmo?";

  return (
    <section style={{ ...CARTAO, borderColor: ligado ? hexA(C.red, 0.5) : C.lineSoft }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: ligado ? C.red : C.textStrong }}>
              {nome}
            </span>
            {/* O estado por escrito, não só pela cor do botão: quem chega
                nesta tela no meio de um incidente precisa ler, não deduzir. */}
            <span
              style={{
                ...ROTULO,
                padding: "2px 7px",
                borderRadius: 999,
                border: `1px solid ${ligado ? hexA(C.red, 0.6) : C.lineStrong}`,
                color: ligado ? C.red : C.textDim,
              }}
            >
              {ligado ? "ligado" : "desligado"}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: C.textSoft, marginTop: 3 }}>{descricao}</div>
        </div>

        <button
          type="button"
          disabled={aplicando}
          onClick={() => {
            if (aplicando) return;
            if (!armado) {
              setArmado(true);
              return;
            }
            setArmado(false);
            onTrocar(destino);
          }}
          style={{
            ...BOTAO_PERIGO,
            minWidth: 132,
            opacity: aplicando ? 0.5 : 1,
            cursor: aplicando ? "progress" : "pointer",
            background: armado ? C.red : "transparent",
            color: armado ? "#fff" : C.red,
            border: `1px solid ${C.red}`,
          }}
        >
          {aplicando ? "aplicando…" : armado ? confirmacao : acao}
        </button>
      </div>
    </section>
  );
}

function Emergencia({
  flags,
  emit,
  minhaSenha,
}: {
  flags: AdminFlags;
  emit: (evento: string, carga: unknown) => void;
  minhaSenha: string;
}): ReactElement {
  /**
   * Chaves esperando resposta do servidor.
   *
   * Some quando `flags` chega — e `flags` chega nos DOIS desfechos, porque o
   * servidor devolve o estado real também quando recusa. Sem isso, uma recusa
   * deixaria o botão preso em "aplicando" para sempre.
   */
  const [aplicando, setAplicando] = useState<string[]>([]);
  useEffect(() => {
    setAplicando([]);
  }, [flags]);

  /*
    Rede de segurança para o caso de resposta nenhuma — conexão que caiu
    entre o clique e a volta. Sem isto o botão ficaria preso em "aplicando"
    até trocar de aba, e um interruptor de emergência travado é exatamente o
    que não pode acontecer numa tela usada no meio de um incidente.
  */
  useEffect(() => {
    if (aplicando.length === 0) return;
    const t = window.setTimeout(() => setAplicando([]), 8000);
    return () => window.clearTimeout(t);
  }, [aplicando]);

  return (
    <div style={{ display: "grid", gap: 12, maxWidth: 680 }}>
      <div style={{ ...ROTULO, color: C.red }}>
        Estes interruptores valem para a plataforma inteira, na hora
      </div>

      {/* Sem senha o servidor recusa, e recusava em silêncio. Avisar antes é
          mais barato do que deixar a pessoa clicar quatro vezes achando que a
          tela quebrou. */}
      {minhaSenha.length === 0 ? (
        <div
          style={{
            padding: "8px 11px",
            borderRadius: 8,
            border: `1px solid ${hexA(C.red, 0.45)}`,
            background: hexA(C.red, 0.08),
            fontSize: 12.5,
            color: C.textSoft,
            lineHeight: 1.45,
          }}
        >
          Mexer num interruptor exige a sua senha. Digite no campo{" "}
          <strong style={{ color: C.textStrong }}>&ldquo;Sua senha&rdquo;</strong>, no topo do
          painel. (Conta que entrou pelo Google não tem senha e passa direto.)
        </div>
      ) : null}

      {FLAGS_EMERGENCIA.map(flag => (
        <Interruptor
          key={flag.chave}
          nome={flag.nome}
          descricao={flag.descricao}
          ligado={flags[flag.chave] === true}
          aplicando={aplicando.includes(flag.chave)}
          onTrocar={paraLigado => {
            setAplicando(atual => [...atual, flag.chave]);
            emit("admin:setFlag", { chave: flag.chave, ligado: paraLigado, senha: minhaSenha });
          }}
        />
      ))}
    </div>
  );
}

function Banidos({
  banidos,
  emit,
}: {
  banidos: AdminBanido[];
  emit: (evento: string, carga: unknown) => void;
}): ReactElement {
  return (
    <section style={{ ...CARTAO, maxWidth: 780 }}>
      <div style={{ ...ROTULO, marginBottom: 8 }}>Contas banidas do LFRCORD</div>
      {banidos.length === 0 ? (
        <div style={{ fontSize: 13, color: C.textDim }}>Ninguém banido.</div>
      ) : (
        banidos.map(b => (
          <div
            key={b.userId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 0",
              borderTop: `1px solid ${C.lineSoft}`,
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: C.textStrong }}>{b.nome}</span>
              <span style={{ color: C.textMuted, marginLeft: 6, fontSize: 12.5 }}>@{b.handle}</span>
              <div style={{ fontSize: 12.5, color: C.textSoft, marginTop: 2 }}>
                {b.motivo ?? "sem motivo registrado"}
              </div>
              <div style={{ ...ROTULO, marginTop: 2 }}>
                por @{b.porHandle} · {data(b.em)} · {b.ate ? `ate ${data(b.ate)}` : "permanente"}
              </div>
            </span>
            <button
              type="button"
              style={BOTAO}
              onClick={() => {
                emit("admin:unban", { userId: b.userId });
                emit("admin:banidos", {});
              }}
            >
              Revogar
            </button>
          </div>
        ))
      )}
    </section>
  );
}

function MeuAcesso(): ReactElement {
  const { state } = useApp();
  const eu = state.me;
  if (!eu) return <div />;
  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 620 }}>
      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Meu acesso</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.textStrong }}>
          {eu.name} <span style={{ color: C.textMuted, fontWeight: 500 }}>@{eu.handle}</span>
        </div>
        <div style={{ ...ROTULO, marginTop: 6, color: C.accent }}>Fundador do LFRCORD</div>
        <div style={{ ...ROTULO, marginTop: 8, letterSpacing: ".04em" }}>id {eu.id}</div>
        <div style={{ ...ROTULO, marginTop: 3, letterSpacing: ".04em" }}>
          conta criada em {data(eu.createdAt)}
        </div>
      </section>
      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 6 }}>Sessões e segurança</div>
        <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.6 }}>
          As sessões desta conta ficam em <strong style={{ color: C.textStrong }}>Configurações → Sessões
          ativas</strong>, junto com o botão de encerrar as outras. A troca de senha fica em{" "}
          <strong style={{ color: C.textStrong }}>Minha conta</strong>. O painel não duplica esses
          controles de propósito — dois lugares para a mesma coisa é como uma delas fica para trás.
        </div>
      </section>
    </div>
  );
}
